import sessionStore from 'client/Session/SessionStore';
import {QuestionModelV3, SubjectModelV2} from 'resources/augmented/Question/QuestionModel.v3';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {getTranslationCoverage, getTranslationSettings} from './Translation.utils';
import {
  fetchTranslatedQuestion,
  fetchTranslatedSupplements,
  getStudentSchoolPermissionsWithoutAssignment,
  getTeacherTranslationPermissions,
  getStudentClassWithSchool,
  fetchQuestionSupplements,
  getAssignmentQuery
} from './Translation.queries';
import {
  ALTERNATE_CONTENT_LANGUAGES,
  AlternateContentLanguage,
  TranslatedQuestion,
  TranslatedSupplement,
  TranslationCoverage
} from './Translation.types';

interface QuestionTranslationState {
  translatedQuestionInfo: TranslatedQuestion | null;
  translatedSupplements: TranslatedSupplement[] | null;
  fetchingTranslations: boolean;
  translationCoverage: TranslationCoverage;
  hasTranslationAccess: boolean;
  isReady: boolean;
  translationSettings: {
    question: boolean;
    supplements: boolean;
  };
  showTranslations: boolean;
  useAssignmentSettings: boolean;
  assignmentSettings: {
    assignment: boolean;
    classroom: boolean;
    student: boolean;
  };
}

interface QuestionTranslationActions {
  fetchTranslation: (
    question: QuestionModelV3,
    subject: SubjectModelV2,
    language?: AlternateContentLanguage
  ) => Promise<void>;
  init: (assignmentId: string | null, isAdaptive?: boolean) => Promise<void>;
  onToggle: () => void;
  setShowTranslations: (showTranslations: boolean) => void;
  reset: () => void;

  // refreshTranslations(): Promise<void>;
}

const initialState: QuestionTranslationState = {
  translatedQuestionInfo: null,
  translatedSupplements: null,
  fetchingTranslations: false,
  translationCoverage: 'none',
  hasTranslationAccess: false,
  useAssignmentSettings: false,
  isReady: false,
  showTranslations: false,
  translationSettings: {
    question: false,
    supplements: false
  },
  assignmentSettings: {
    assignment: false,
    classroom: false,
    student: false
  }
};

export const useQuestionTranslationStore = create<
  QuestionTranslationState & QuestionTranslationActions
>()(
  devtools((set, get) => ({
    ...initialState,
    reset: () => {
      set(initialState);
    },
    init: async (assignmentId, isAdaptive = false) => {
      if (sessionStore.isStudent()) {
        const studentId = sessionStore.getUserId();
        let hasPermission = false;
        let useAssignmentSettings = false;
        if (isAdaptive || !assignmentId) {
          // In non assignment student view
          hasPermission = await getStudentSchoolPermissionsWithoutAssignment(studentId);
        } else if (assignmentId) {
          // In assignment view
          const classroom = await getStudentClassWithSchool(assignmentId, studentId);
          const school = classroom?.getSchool?.();
          hasPermission = school?.isAllowTranslations() || false;

          useAssignmentSettings = true; // only use assignment settings if in an assignment view
          const classroomId = classroom?.getId?.();
          const assignment = await getAssignmentQuery(
            assignmentId,
            classroomId,
            studentId
          ).getResourcePromise();

          set({
            assignmentSettings: {
              assignment: assignment?.getIsAllowTranslationFromSettings?.() || false,
              classroom: assignment?.getIsAllowTranslationFromSettings?.({classroomId}) || false,
              student:
                assignment?.getIsAllowTranslationFromSettings?.({classroomId, studentId}) || false
            }
          });
        }

        set({
          isReady: true,
          hasTranslationAccess: hasPermission,
          useAssignmentSettings
        });
      } else if (sessionStore.isSchoolAdministrator() || sessionStore.isTeacher()) {
        let hasPermission = true;

        const teacherId = sessionStore.getUserId();
        hasPermission = await getTeacherTranslationPermissions(teacherId);

        set({
          isReady: true,
          hasTranslationAccess: hasPermission
        });
      } else {
        set({
          isReady: true,
          hasTranslationAccess: false
        });
      }
    },
    onToggle: () => {
      const currentStatus = get().showTranslations;
      set({
        showTranslations: !currentStatus
      });
    },
    setShowTranslations: (showTranslations) => {
      set({
        showTranslations
      });
    },
    fetchTranslation: async (question, subject, language = ALTERNATE_CONTENT_LANGUAGES[0]) => {
      if (get().isReady && !get().hasTranslationAccess) {
        // Don't fetch if no access to translation info
        return;
      }

      const isInDennis = /dennis\/content/.test(window.location.pathname);

      set({
        fetchingTranslations: true,
        // reset toggles to false
        ...(!isInDennis ? {showTranslations: false} : {})
      });
      const supplements = await fetchQuestionSupplements(question.getId());

      const supplementIds = supplements.map((s) => s.getId()).toJS();

      const [{info}, translatedSupplements] = await Promise.all([
        fetchTranslatedQuestion(question.getId(), language),
        fetchTranslatedSupplements(supplementIds)
      ]);

      const translationCoverage = getTranslationCoverage(info, translatedSupplements, {
        question,
        subject,
        supplements
      });

      const translationSettings = getTranslationSettings({
        question,
        subject,
        supplements
      });
      set({
        translatedSupplements,
        translationCoverage, // TODO: change to match api field,
        // reflect this status on FE instead of just a can view toggle
        translatedQuestionInfo: info,
        fetchingTranslations: false,
        translationSettings: {
          question: translationSettings.question,
          supplements: translationSettings.supplement
        }
      });
    }
  }))
);
